import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useQueries } from '@tanstack/react-query';

import { getProduct } from '../../api/catalogProductsApi';
import { formatPrice } from '../../lib/format';
import { retagChecklistLines } from '../../lib/priceListCoverage';
import { catalogKeys } from '../../hooks/queryKeys';
import type { PriceLineResponse } from '../../model/types';

export interface PriceListRetagChecklistDrawerProps {
  open: boolean;
  storeName: string;
  /** The bound list's own lines — filtered internally down to the ones matching `validFrom`. */
  lines: PriceLineResponse[];
  validFrom: string | null;
  onClose: () => void;
}

/**
 * Printable re-tag checklist (design spec §8.12) — the re-tag lead-time signal made actionable:
 * staff tick off shelf labels before a scheduled price change lands. Checkboxes are session-local
 * ticking aids only (never persisted, per the spec's own v1 scope cut).
 */
export function PriceListRetagChecklistDrawer({
  open,
  storeName,
  lines,
  validFrom,
  onClose,
}: PriceListRetagChecklistDrawerProps) {
  const { t } = useTranslation('catalog');
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const items = useMemo(
    () => (validFrom ? retagChecklistLines(lines, validFrom) : []),
    [lines, validFrom],
  );

  const productQueries = useQueries({
    queries: items.map((item) => ({
      queryKey: catalogKeys.product(item.productId),
      queryFn: () => getProduct(item.productId),
      enabled: open,
      staleTime: 5 * 60_000,
    })),
  });

  const toggle = (productId: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 480 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box id="retag-checklist-print" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <style>
          {`@media print {
            body * { visibility: hidden; }
            #retag-checklist-print, #retag-checklist-print * { visibility: visible; }
            #retag-checklist-print { position: absolute; top: 0; left: 0; width: 100%; }
            #retag-checklist-noprint { display: none !important; }
          }`}
        </style>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('priceListCoverage.retagTitle', { store: storeName })}
            </Typography>
            {validFrom ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('priceListCoverage.retagDescription', {
                  date: new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
                    new Date(validFrom),
                  ),
                })}
              </Typography>
            ) : null}
          </Box>
          <Button
            id="retag-checklist-noprint"
            startIcon={<PrintRoundedIcon />}
            onClick={() => window.print()}
            disabled={items.length === 0}
          >
            {t('priceListCoverage.retagPrint')}
          </Button>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('priceListCoverage.retagEmpty')}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {items.map((item, index) => {
                const productName = productQueries[index]?.data?.name ?? item.productId;
                return (
                  <FormControlLabel
                    key={item.productId}
                    control={
                      <Checkbox
                        checked={checked.has(item.productId)}
                        onChange={() => toggle(item.productId)}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                        <Typography variant="body2">{productName}</Typography>
                        <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace' }} color="text.secondary">
                          {item.oldAmount !== undefined ? formatPrice(item.oldAmount) : '—'} →{' '}
                          {formatPrice(item.newAmount)}
                        </Typography>
                      </Stack>
                    }
                  />
                );
              })}
            </Stack>
          )}
        </Box>

        <Box
          id="retag-checklist-noprint"
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}
        >
          <Button onClick={onClose}>{t('priceListCoverage.retagClose')}</Button>
        </Box>
      </Box>
    </Drawer>
  );
}
